import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { LogOut, LayoutDashboard, Layers, Home, Menu, X, Rocket, Inbox } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminNavbar = ({ showHubLink = true, showInboxLink = true }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!showInboxLink) return;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/contact/unread-count`, { withCredentials: true });
        if (active) setUnreadCount(res.data.count || 0);
      } catch (err) {
        // Non-super-admins (403) or transient errors: simply show no badge
        if (active) setUnreadCount(0);
      }
    })();
    return () => { active = false; };
  }, [showInboxLink, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ...(showHubLink ? [{ path: '/admin/hub', label: 'App Hub', icon: Layers }] : []),
    { path: '/admin/studio', label: 'App Studio', icon: Rocket },
    ...(showInboxLink ? [{ path: '/admin/inbox', label: 'Inbox', icon: Inbox }] : []),
    { path: '/', label: 'Main Site', icon: Home },
  ];

  const handleNavClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-[#302f2c] border-b border-[#1e3a5f]/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2">
            <span className="text-[#7dd3fc] font-black text-xl md:text-2xl tracking-tight">RDK</span>
            <span className="text-white font-medium text-base md:text-lg">Admin</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                variant="ghost"
                className={`rounded-full px-4 py-2 text-sm flex items-center gap-2 transition-all ${
                  isActive(item.path)
                    ? 'bg-[#7dd3fc] text-[#1a1c1b]'
                    : 'text-[#888680] hover:text-[#7dd3fc] hover:bg-[#1e3a5f]/30'
                }`}
              >
                <item.icon size={16} />
                {item.label}
                {item.path === '/admin/inbox' && unreadCount > 0 && (
                  <span
                    data-testid="inbox-unread-badge"
                    className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                  >
                    {unreadCount}
                  </span>
                )}
              </Button>
            ))}
          </nav>
          
          {/* Desktop User Info & Logout */}
          <div className="hidden md:flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-3">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                )}
                <span className="text-[#7dd3fc] text-sm">{user.name}</span>
              </div>
            )}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] hover:border-[#7dd3fc]/50 rounded-full px-4 py-2 text-sm"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-[#7dd3fc] p-2"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-[#1e3a5f]/50">
            {/* User Info */}
            {user && (
              <div className="flex items-center gap-3 px-2 pb-4 mb-4 border-b border-[#1e3a5f]/50">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                )}
                <div>
                  <p className="text-[#7dd3fc] font-medium">{user.name}</p>
                  <p className="text-[#888680] text-xs">{user.email}</p>
                </div>
              </div>
            )}

            {/* Nav Links */}
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 transition-all ${
                    isActive(item.path)
                      ? 'bg-[#7dd3fc] text-[#1a1c1b]'
                      : 'text-[#7dd3fc] hover:bg-[#1e3a5f]/30'
                  }`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.label}</span>
                  {item.path === '/admin/inbox' && unreadCount > 0 && (
                    <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full mt-4 border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] hover:border-[#7dd3fc]/50 rounded-full py-3"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

export default AdminNavbar;
